import type {
    ProfileAskDonePayload,
    ProfileAskSSEEvent,
} from '@/types/profileAsk'

const API_BASE = import.meta.env.VITE_API_URL || ''

export interface ProfileAskStreamOptions {
    conversationId: number
    question: string
    signal?: AbortSignal
    onProgress?: (event: string) => void
    onToken?: (content: string) => void
    onDone?: (payload: ProfileAskDonePayload) => void
    onError?: (message: string) => void
}

async function getAuthHeaders(): Promise<Record<string, string>> {
    const { default: store } = await import('@/store')
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
    }

    const impersonate = sessionStorage.getItem('impersonating')
    if (impersonate) {
        headers['X-Switch-User'] = impersonate
        const workspace = sessionStorage.getItem('impersonatingWorkspace')
        if (workspace) {
            headers['X-Switch-User-Workspace'] = workspace
        }
    }

    let token = store.state.auth.token
    const valid = store.getters.isRefreshTokenValid

    if (!valid || !token) {
        await store.dispatch('refreshToken').catch(() => undefined)
        token = store.getters.token
    }

    if (token) {
        headers.Authorization = `Bearer ${token}`
    }

    return headers
}

async function logoutAndRedirectToLogin(): Promise<void> {
    const { default: store } = await import('@/store')
    const { default: router } = await import('@/router')
    await store.dispatch('logout').catch(() => undefined)
    router.push({ name: 'Login' })
}

/**
 * Mirrors axios-auth-refresh: on 401, refresh once and retry once;
 * if that fails, logout and send the user to Login.
 */
async function fetchWithAuthRetry(
    url: string,
    init: RequestInit,
    retried = false,
): Promise<Response> {
    const headers = await getAuthHeaders()
    const response = await fetch(url, {
        ...init,
        headers: {
            ...headers,
            ...(init.headers as Record<string, string> | undefined),
        },
    })

    if (response.status !== 401) {
        return response
    }

    if (retried) {
        await logoutAndRedirectToLogin()
        throw new Error('Unauthorized')
    }

    const { default: store } = await import('@/store')

    try {
        await store.dispatch('refreshToken', { silent: true })
    } catch {
        await logoutAndRedirectToLogin()
        throw new Error('Unauthorized')
    }

    // refreshToken may resolve even after authenticateError — verify session
    if (!store.getters.token || !store.getters.isRefreshTokenValid) {
        await logoutAndRedirectToLogin()
        throw new Error('Unauthorized')
    }

    return fetchWithAuthRetry(url, init, true)
}

function parseSSELine(line: string): ProfileAskSSEEvent | null {
    const trimmed = line.trim()
    if (!trimmed.startsWith('data:')) {
        return null
    }

    const json = trimmed.slice(5).trim()
    if (!json) {
        return null
    }

    try {
        return JSON.parse(json) as ProfileAskSSEEvent
    } catch {
        return null
    }
}

export async function streamProfileAskQuestion(
    options: ProfileAskStreamOptions,
): Promise<ProfileAskDonePayload> {
    const url = `${API_BASE}/api/profile-ask/conversations/${options.conversationId}/messages`

    const response = await fetchWithAuthRetry(url, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({ question: options.question }),
        signal: options.signal,
    })

    if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After')
        const message = retryAfter
            ? `Too many requests. Please wait ${retryAfter} seconds and try again.`
            : 'Too many requests. Please wait a moment and try again.'
        options.onError?.(message)
        throw new Error(message)
    }

    if (!response.ok) {
        let message = 'Something went wrong. Please try again.'
        try {
            const body = await response.json()
            message = body?.error ?? message
        } catch {
            // ignore parse errors
        }
        options.onError?.(message)
        throw new Error(message)
    }

    if (!response.body) {
        const message = 'No response received from the server.'
        options.onError?.(message)
        throw new Error(message)
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    return new Promise((resolve, reject) => {
        const processChunk = async (): Promise<void> => {
            try {
                const { done, value } = await reader.read()

                if (done) {
                    reject(new Error('Stream ended without a response.'))
                    return
                }

                buffer += decoder.decode(value, { stream: true })
                const parts = buffer.split('\n\n')
                buffer = parts.pop() ?? ''

                for (const part of parts) {
                    for (const line of part.split('\n')) {
                        const event = parseSSELine(line)
                        if (!event) {
                            continue
                        }

                        if (event.event === 'error') {
                            const message =
                                event.message ??
                                'Something went wrong. Please try again.'
                            options.onError?.(message)
                            reject(new Error(message))
                            return
                        }

                        if (
                            (event.event === 'token' || event.event === 'content') &&
                            typeof event.content === 'string' &&
                            event.content.length > 0
                        ) {
                            options.onToken?.(event.content)
                            continue
                        }

                        if (event.event === 'done' && event.payload) {
                            options.onDone?.(event.payload)
                            resolve(event.payload)
                            return
                        }

                        options.onProgress?.(event.event)
                    }
                }

                await processChunk()
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    reject(error)
                    return
                }
                reject(error)
            }
        }

        processChunk()
    })
}